import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment-timezone'
import {
  Button,
  Table,
  Badge,
  Form,
  Card,
  Accordion,
} from 'react-bootstrap'
import ModalContainer from './ModalContainer'
import Loader from '../../participant_module/components/Loader'
import Message from '../../participant_module/components/Message'

import { getDecisionRulesUsage, evaluateFunction } from '../actions/jitaiActions'
import {
  JITAI_RULES_USAGE_RESET,
  JITAI_EVALUATE_RESET,
} from '../constants/jitaiConstants'

const RuleTable = ({ rules, columns }) => {
  if (!rules || rules.length === 0) {
    return <p className='text-muted mb-0'>(none configured)</p>
  }

  return (
    <Table bordered hover responsive className='table-sm mb-0'>
      <thead>
        <tr>
          {columns.map((col) => (
            <th key={col.key}>{col.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rules.map((rule, idx) => (
          <tr key={idx}>
            {columns.map((col) => (
              <td key={col.key}>{col.render(rule)}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </Table>
  )
}

const RegisteredBadge = ({ registered }) => (
  <Badge bg={registered ? 'success' : 'secondary'}>
    {registered ? 'Registered' : 'Not Registered'}
  </Badge>
)

const LogDisplay = ({ log }) => {
  if (!log || log.length === 0) return null
  return (
    <pre
      style={{
        backgroundColor: '#f8f9fa',
        border: '1px solid #dee2e6',
        borderRadius: '4px',
        padding: '10px',
        fontSize: '0.85rem',
        maxHeight: '300px',
        overflowY: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        marginBottom: 0,
      }}
    >
      {log.map((entry, i) => (
        <div key={i}>{entry}</div>
      ))}
    </pre>
  )
}

const formatResult = (result) => {
  if (result === true) return 'TRUE'
  if (result === false) return 'FALSE'
  if (result === null || result === undefined) return 'NULL'
  return String(result)
}

/** Personalized JITAI message rules: API may include resultMessageCode next to ObjectId result */
const formatEvaluateMainResult = (evaluateResult) => {
  if (!evaluateResult) return ''
  const base = formatResult(evaluateResult.result)
  if (evaluateResult.resultMessageCode) {
    return `${base} (${evaluateResult.resultMessageCode})`
  }
  return base
}

const getResultColor = (result) => {
  if (result === true) return 'green'
  if (result === false) return 'red'
  return '#333'
}

const sectionHeaderStyle = {
  fontSize: '1rem',
  fontWeight: 'bold',
  marginTop: '16px',
  marginBottom: '4px',
}

const sectionDescStyle = {
  fontSize: '0.85rem',
  color: '#6c757d',
  marginBottom: '8px',
}

const headingStyle = {
  fontSize: '1.1rem',
}

const groupAssignmentCols = [
  { key: 'code', label: 'Rule Code', render: (r) => r.code },
  { key: 'status', label: 'Status', render: (r) => <RegisteredBadge registered={r.registered} /> },
  { key: 'definition', label: 'Definition', render: (r) => r.definition || '—' },
  {
    key: 'vars',
    label: 'Dependent Variables',
    render: (r) =>
      r.dependentVariables.length > 0 ? r.dependentVariables.join(', ') : '—',
  },
  {
    key: 'target',
    label: 'Assigned Group (on pass)',
    render: (r) =>
      r.assignedGroup
        ? `${r.assignedGroup.name} (#${r.assignedGroup.groupNum})`
        : '—',
  },
]

const optionalTxCols = [
  { key: 'code', label: 'Rule Code', render: (r) => r.code },
  { key: 'status', label: 'Status', render: (r) => <RegisteredBadge registered={r.registered} /> },
  { key: 'definition', label: 'Definition', render: (r) => r.definition || '—' },
  {
    key: 'vars',
    label: 'Dependent Variables',
    render: (r) =>
      r.dependentVariables.length > 0 ? r.dependentVariables.join(', ') : '—',
  },
  {
    key: 'scopeGroup',
    label: 'Scope Group',
    render: (r) => r.optionalModulesParticipantGroup?.name || '—',
  },
  {
    key: 'packagesThen',
    label: 'Then Assign',
    render: (r) =>
      r.packagesAdded && r.packagesAdded.length > 0
        ? r.packagesAdded.join(', ')
        : '—',
  },
  {
    key: 'packagesElse',
    label: 'Else Assign',
    render: (r) =>
      r.packagesElse && r.packagesElse.length > 0
        ? r.packagesElse.join(', ')
        : '—',
  },
]

const presentMessageCols = [
  { key: 'code', label: 'Rule Code', render: (r) => r.code },
  { key: 'status', label: 'Status', render: (r) => <RegisteredBadge registered={r.registered} /> },
  { key: 'definition', label: 'Definition', render: (r) => r.definition || '—' },
  {
    key: 'vars',
    label: 'Dependent Variables',
    render: (r) =>
      r.dependentVariables.length > 0 ? r.dependentVariables.join(', ') : '—',
  },
  { key: 'setting', label: 'Used In', render: (r) => r.usedInJitaiSetting || '—' },
]

const personalizedCols = [
  { key: 'code', label: 'Rule Code', render: (r) => r.code },
  { key: 'status', label: 'Status', render: (r) => <RegisteredBadge registered={r.registered} /> },
  { key: 'definition', label: 'Definition', render: (r) => r.definition || '—' },
  {
    key: 'vars',
    label: 'Dependent Variables',
    render: (r) =>
      r.dependentVariables.length > 0 ? r.dependentVariables.join(', ') : '—',
  },
  {
    key: 'messages',
    label: 'Candidate Messages',
    render: (r) =>
      r.candidateMessages && r.candidateMessages.length > 0
        ? r.candidateMessages.map((m) => m.code).join(', ')
        : '—',
  },
  {
    key: 'setting',
    label: 'Used In',
    render: (r) => `${r.usedInJitaiSetting || '—'} (${r.probability}%)`,
  },
]

const customRespCols = [
  { key: 'code', label: 'Response Code', render: (r) => r.code },
  { key: 'status', label: 'Status', render: (r) => <RegisteredBadge registered={r.registered} /> },
  { key: 'notes', label: 'Implementation Notes', render: (r) => r.customImplementationNotes || '—' },
  { key: 'setting', label: 'Used In', render: (r) => r.usedInJitaiSetting || '—' },
  {
    key: 'collected',
    label: 'Collected At',
    render: (r) => `${r.collectedAt} min after task acceptance`,
  },
]

// ─── Component 1: Active Rules & Responses ───────────────────────────────────

export const ActiveRulesViewer = ({ email, studyId, participantId }) => {
  const dispatch = useDispatch()

  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo, logout } = userLogin

  const jitaiRulesUsage = useSelector((state) => state.jitaiRulesUsage)
  const {
    loading: loadingUsage,
    error: errorUsage,
    success: successUsage,
    usage,
  } = jitaiRulesUsage

  const [show, setShow] = useState(false)

  const handleOpen = () => {
    const now = new Date()
    if (userInfo && userInfo.expires > now && !logout) {
      if (studyId && participantId && !successUsage && !loadingUsage && !errorUsage) {
        dispatch(getDecisionRulesUsage(studyId, participantId))
      }
    }
    setShow(true)
  }

  const handleClose = () => {
    dispatch({ type: JITAI_RULES_USAGE_RESET })
    setShow(false)
  }

  return (
    <>
      <Button variant='outline-info' className='btn-sm m-1' onClick={handleOpen}>
        Active Rules & Responses
      </Button>

      <ModalContainer
        title='Active Rules & Responses'
        show={show}
        handleClose={handleClose}
      >
        {errorUsage && <Message variant='danger'>{errorUsage}</Message>}

        {loadingUsage ? (
          <Loader />
        ) : (
          successUsage &&
          usage && (
            <>
              <div className='mb-3'>
                <h2 style={headingStyle} className='mb-1'>
                  Participant: {email}
                </h2>
                <p className='mb-0' style={{ fontSize: '0.9rem' }}>
                  Current Group:{' '}
                  <strong>
                    {usage.currentGroup.name} (#{usage.currentGroup.groupNum})
                  </strong>
                  {usage.currentStage && (
                    <>
                      {' | Stage: '}
                      <strong>
                        {usage.currentStage.name} (#{usage.currentStage.stageNum})
                      </strong>
                    </>
                  )}
                </p>
              </div>

              <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                <h5 style={sectionHeaderStyle}>1. Group Assignment</h5>
                <p style={sectionDescStyle}>
                  Rules that determine when this participant advances to the
                  next stage/group.
                </p>
                <RuleTable rules={usage.groupAssignment} columns={groupAssignmentCols} />

                <h5 style={sectionHeaderStyle}>
                  2. Present Optional Chapters in Next Stage
                </h5>
                <p style={sectionDescStyle}>
                  Rules that determine which optional treatment packages to
                  assign when this participant advances. Each rule assigns a
                  &quot;then&quot; list when the rule passes, or an optional
                  &quot;else&quot; list when it fails.
                </p>
                <RuleTable
                  rules={usage.optionalTxPackageAssignment}
                  columns={optionalTxCols}
                />

                <h5 style={sectionHeaderStyle}>
                  3. Present JITAI Message Gate
                </h5>
                <p style={sectionDescStyle}>
                  Rules that determine whether a JITAI message should be
                  presented at all when this participant accepts a task.
                </p>
                <RuleTable
                  rules={usage.presentJitaiMessage}
                  columns={presentMessageCols}
                />

                <h5 style={sectionHeaderStyle}>
                  4. Picking Which Personalized Message
                </h5>
                <p style={sectionDescStyle}>
                  Rules that select which specific JITAI message to show from
                  a candidate list for this participant.
                </p>
                <RuleTable
                  rules={usage.personalizedJitaiMessage}
                  columns={personalizedCols}
                />

                <h5 style={sectionHeaderStyle}>
                  5. Custom JITAI Response Computation
                </h5>
                <p style={sectionDescStyle}>
                  Custom response implementations for collecting
                  non-questionnaire data after this participant accepts a
                  task.
                </p>
                <RuleTable
                  rules={usage.customResponses}
                  columns={customRespCols}
                />
              </div>
            </>
          )
        )}
      </ModalContainer>
    </>
  )
}

// ─── Component 2: Evaluate Custom Implementation ─────────────────────────────

const getAvailableFunctions = (usage) => {
  if (!usage) return []
  if (Array.isArray(usage.evaluableFunctions)) {
    return usage.evaluableFunctions
  }

  // Legacy fallback: group-scoped usage only
  const functions = []

  for (const rule of usage.groupAssignment || []) {
    if (rule.registered) {
      functions.push({
        functionType: 'decisionRule',
        code: rule.code,
        ruleType: 'groupAssignment',
        label: `[Decision Rule] ${rule.code} (groupAssignment)`,
      })
    }
  }
  for (const rule of usage.optionalTxPackageAssignment || []) {
    if (rule.registered) {
      functions.push({
        functionType: 'decisionRule',
        code: rule.code,
        ruleType: 'optionalTxPackageAssignment',
        label: `[Decision Rule] ${rule.code} (optionalTxPackageAssignment)`,
      })
    }
  }
  for (const rule of usage.presentJitaiMessage || []) {
    if (rule.registered) {
      functions.push({
        functionType: 'decisionRule',
        code: rule.code,
        ruleType: 'presentJitaiMessage',
        label: `[Decision Rule] ${rule.code} (presentJitaiMessage)`,
      })
    }
  }
  for (const rule of usage.personalizedJitaiMessage || []) {
    if (rule.registered) {
      functions.push({
        functionType: 'decisionRule',
        code: rule.code,
        ruleType: 'personalizedJitaiMessage',
        label: `[Decision Rule] ${rule.code} (personalizedJitaiMessage)`,
      })
    }
  }
  for (const v of usage.variables || []) {
    if (v.registered) {
      functions.push({
        functionType: 'variable',
        code: v.code,
        ruleType: '',
        label: `[Variable] ${v.code}`,
      })
    }
  }
  for (const cr of usage.customResponses || []) {
    if (cr.registered) {
      functions.push({
        functionType: 'customResp',
        code: cr.code,
        ruleType: '',
        label: `[Custom Response] ${cr.code}`,
      })
    }
  }

  return functions
}

const CUSTOM_RESP_ANCHOR_TZ = 'Asia/Hong_Kong'

/** Value for `<input type="datetime-local" />` interpreted as Hong Kong time. */
const formatHktForDatetimeLocal = (date = new Date()) =>
  moment(date).tz(CUSTOM_RESP_ANCHOR_TZ).format('YYYY-MM-DDTHH:mm')

/** Parse datetime-local string as Hong Kong wall time → UTC ms. */
const hktDatetimeLocalToUtcMs = (value) => {
  if (!value) return null
  const m = moment.tz(value, 'YYYY-MM-DDTHH:mm', CUSTOM_RESP_ANCHOR_TZ)
  return m.isValid() ? m.valueOf() : null
}

export const EvaluateCustomImplementation = ({ email, studyId, participantId }) => {
  const dispatch = useDispatch()

  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo, logout } = userLogin

  const jitaiRulesUsage = useSelector((state) => state.jitaiRulesUsage)
  const {
    loading: loadingUsage,
    error: errorUsage,
    success: successUsage,
    usage,
  } = jitaiRulesUsage

  const jitaiEvaluate = useSelector((state) => state.jitaiEvaluate)
  const {
    loading: loadingEvaluate,
    error: errorEvaluate,
    success: successEvaluate,
    evaluateResult,
  } = jitaiEvaluate

  const [show, setShow] = useState(false)
  const [selectedFunctionType, setSelectedFunctionType] = useState('')
  const [selectedCode, setSelectedCode] = useState('')
  const [selectedRuleType, setSelectedRuleType] = useState('')
  const [customRespAnchorHkt, setCustomRespAnchorHkt] = useState(() =>
    formatHktForDatetimeLocal()
  )
  const [customRespPhase, setCustomRespPhase] = useState('pre_message')

  const handleOpen = () => {
    const now = new Date()
    if (userInfo && userInfo.expires > now && !logout) {
      if (studyId && participantId && !successUsage && !loadingUsage && !errorUsage) {
        dispatch(getDecisionRulesUsage(studyId, participantId))
      }
    }
    setShow(true)
  }

  const handleClose = () => {
    dispatch({ type: JITAI_RULES_USAGE_RESET })
    dispatch({ type: JITAI_EVALUATE_RESET })
    setSelectedFunctionType('')
    setSelectedCode('')
    setSelectedRuleType('')
    setCustomRespAnchorHkt(formatHktForDatetimeLocal())
    setCustomRespPhase('pre_message')
    setShow(false)
  }

  const availableFunctions = getAvailableFunctions(usage)
  const selectedIdx = availableFunctions.findIndex(
    (f) =>
      f.functionType === selectedFunctionType &&
      f.code === selectedCode &&
      f.ruleType === selectedRuleType
  )

  const handleFunctionSelect = (e) => {
    const idx = parseInt(e.target.value)
    dispatch({ type: JITAI_EVALUATE_RESET })
    if (isNaN(idx) || idx < 0) {
      setSelectedFunctionType('')
      setSelectedCode('')
      setSelectedRuleType('')
      return
    }
    if (idx < availableFunctions.length) {
      const fn = availableFunctions[idx]
      setSelectedFunctionType(fn.functionType)
      setSelectedCode(fn.code)
      setSelectedRuleType(fn.ruleType)
      if (fn.functionType === 'customResp') {
        setCustomRespAnchorHkt(formatHktForDatetimeLocal())
      }
    }
  }

  const handleEvaluate = () => {
    if (!selectedFunctionType || !selectedCode) return

    const body = {
      participantId,
      functionType: selectedFunctionType,
      code: selectedCode,
      ruleType: selectedRuleType || undefined,
    }

    if (selectedFunctionType === 'customResp') {
      const utcMs = hktDatetimeLocalToUtcMs(customRespAnchorHkt)
      if (utcMs == null) return
      body.metadata = {
        phase: customRespPhase,
        utc_ms: utcMs,
      }
    }

    dispatch(evaluateFunction(studyId, body))
  }

  const customRespAnchorUtcMs = hktDatetimeLocalToUtcMs(customRespAnchorHkt)
  const isCustomRespSelected = selectedFunctionType === 'customResp'

  return (
    <>
      <Button variant='outline-warning' className='btn-sm m-1' onClick={handleOpen}>
        Evaluate Custom Implementation
      </Button>

      <ModalContainer
        title='Evaluate Custom Implementation'
        show={show}
        handleClose={handleClose}
      >
        {errorUsage && <Message variant='danger'>{errorUsage}</Message>}

        {loadingUsage ? (
          <Loader />
        ) : (
          successUsage &&
          usage && (
            <>
              <div className='mb-3'>
                <h2 style={headingStyle} className='mb-1'>
                  Participant: {email}
                </h2>
                <p className='mb-0' style={{ fontSize: '0.9rem' }}>
                  Current Group:{' '}
                  <strong>
                    {usage.currentGroup.name} (#{usage.currentGroup.groupNum})
                  </strong>
                  {usage.currentStage && (
                    <>
                      {' | Stage: '}
                      <strong>
                        {usage.currentStage.name} (#{usage.currentStage.stageNum})
                      </strong>
                    </>
                  )}
                </p>
              </div>

              <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {availableFunctions.length === 0 ? (
                  <Message variant='info'>
                    No implemented functions registered in code for this study.
                  </Message>
                ) : (
                  <>
                    <Form.Group className='mb-3'>
                      <Form.Label>
                        <strong>Select function to evaluate</strong>
                      </Form.Label>
                      <Form.Control
                        as='select'
                        value={selectedIdx >= 0 ? selectedIdx : ''}
                        onChange={handleFunctionSelect}
                      >
                        <option value=''>-- Select --</option>
                        {availableFunctions.map((fn, idx) => (
                          <option key={idx} value={idx}>
                            {fn.label}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>

                    {isCustomRespSelected && (
                      <>
                        <Form.Group className='mb-3'>
                          <Form.Label>
                            <strong>Anchor time (HKT)</strong>
                          </Form.Label>
                          <Form.Control
                            type='datetime-local'
                            value={customRespAnchorHkt}
                            onChange={(e) => setCustomRespAnchorHkt(e.target.value)}
                          />
                          <Form.Text className='text-muted'>
                            Anchor for which participant state to read (
                            <code>metadata.utc_ms</code>): pre_message =
                            notification time; proximal_outcome = accept time
                            plus &quot;Minutes After Task&quot; from the
                            JitaiSetting. The nightly job runs later but will
                            not process the task until this anchor is in the
                            past. Custom responses that use diary data may
                            derive the target <code>for_diary_ax_date</code>{' '}
                            from this anchor (e.g. HK calendar day + offset —
                            see each response&apos;s implementation notes).
                            {customRespAnchorUtcMs != null && (
                              <>
                                {' '}
                                UTC:{' '}
                                {new Date(customRespAnchorUtcMs).toISOString()}
                              </>
                            )}
                          </Form.Text>
                        </Form.Group>

                        <Form.Group className='mb-3'>
                          <Form.Label>
                            <strong>Phase</strong>
                          </Form.Label>
                          <Form.Control
                            as='select'
                            value={customRespPhase}
                            onChange={(e) => setCustomRespPhase(e.target.value)}
                          >
                            <option value='pre_message'>pre_message</option>
                            <option value='proximal_outcome'>proximal_outcome</option>
                          </Form.Control>
                          <Form.Text className='text-muted'>
                            Passed through as <code>metadata.phase</code> (scopes
                            how the observation is saved; does not change
                            retrieval logic for all custom responses).
                          </Form.Text>
                        </Form.Group>
                      </>
                    )}

                    <Button
                      variant='primary'
                      className='mb-3'
                      disabled={
                        !selectedFunctionType ||
                        !selectedCode ||
                        loadingEvaluate ||
                        (isCustomRespSelected && customRespAnchorUtcMs == null)
                      }
                      onClick={handleEvaluate}
                    >
                      {loadingEvaluate ? 'Evaluating...' : 'Evaluate Now'}
                    </Button>

                    {errorEvaluate && (
                      <Message variant='danger'>{errorEvaluate}</Message>
                    )}

                    {loadingEvaluate && <Loader />}

                    {successEvaluate && evaluateResult && (
                      <Card className='mt-2'>
                        <Card.Body>
                          <div className='mb-3'>
                            <strong>Result: </strong>
                            <span
                              style={{
                                fontSize: '1.1rem',
                                fontWeight: 'bold',
                                color: getResultColor(evaluateResult.result),
                              }}
                            >
                              {formatEvaluateMainResult(evaluateResult)}
                            </span>
                          </div>

                          {evaluateResult.variableResults &&
                            Object.keys(evaluateResult.variableResults)
                              .length > 0 && (
                              <Accordion className='mb-3' defaultActiveKey='0'>
                                <Card>
                                  <Card.Header>
                                    <Accordion.Toggle
                                      as={Button}
                                      variant='link'
                                      eventKey='0'
                                      className='p-0 text-left text-decoration-none'
                                    >
                                      Dependent Variable Results (
                                      {
                                        Object.keys(
                                          evaluateResult.variableResults
                                        ).length
                                      }
                                      )
                                    </Accordion.Toggle>
                                  </Card.Header>
                                  <Accordion.Collapse eventKey='0'>
                                    <Card.Body>
                                      {Object.entries(
                                        evaluateResult.variableResults
                                      ).map(([varCode, varData]) => (
                                        <div key={varCode} className='mb-2'>
                                          <strong>{varCode}:</strong>{' '}
                                          <span
                                            style={{
                                              color: getResultColor(
                                                varData.result
                                              ),
                                            }}
                                          >
                                            {formatResult(varData.result)}
                                          </span>
                                          {varData.log &&
                                            varData.log.length > 0 && (
                                              <LogDisplay log={varData.log} />
                                            )}
                                        </div>
                                      ))}
                                    </Card.Body>
                                  </Accordion.Collapse>
                                </Card>
                              </Accordion>
                            )}

                          {evaluateResult.evaluateDetails &&
                            evaluateResult.evaluateDetails.length > 0 && (
                              <div className='mb-3'>
                                <strong>evaluateDetails (groupAssignment):</strong>
                                <Table
                                  bordered
                                  size='sm'
                                  className='mt-1 mb-0'
                                >
                                  <thead>
                                    <tr>
                                      <th>condition</th>
                                      <th>result</th>
                                      <th>params</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {evaluateResult.evaluateDetails.map(
                                      (row, idx) => (
                                        <tr key={idx}>
                                          <td>{row.condition}</td>
                                          <td
                                            style={{
                                              color: getResultColor(row.result),
                                            }}
                                          >
                                            {formatResult(row.result)}
                                          </td>
                                          <td>
                                            {row.params
                                              ? JSON.stringify(row.params)
                                              : '—'}
                                          </td>
                                        </tr>
                                      )
                                    )}
                                  </tbody>
                                </Table>
                              </div>
                            )}

                          {evaluateResult.log &&
                            evaluateResult.log.length > 0 && (
                              <div>
                                <strong>Log:</strong>
                                <LogDisplay log={evaluateResult.log} />
                              </div>
                            )}
                        </Card.Body>
                      </Card>
                    )}
                  </>
                )}
              </div>
            </>
          )
        )}
      </ModalContainer>
    </>
  )
}
